import {
    IService,
    IUpdatable,
    IUpdateManager,
    SystemComponentType,
    UpdateFunction
} from '@gongxigames/framework/core/types';
import {injectable} from '@gongxigames/framework/core/installer';
import {Updatable} from '@gongxigames/framework/core/fragments/update/Updatable';
import {Timer} from '@gongxigames/framework/core/fragments/update/Timer';
import {Tween} from '@gongxigames/framework/core/fragments/tween/Tween';
import {CoreDI} from '@gongxigames/framework/core/configs/CoreDI';
import {abortablePromise} from '@gongxigames/framework/lib/util/utilPromise';

type AsyncTimerOptions = {
    abortSignal: AbortSignal;
};

@injectable()
export class UpdateManager extends Updatable implements IUpdateManager, IService {
    public static NAME: symbol = CoreDI.UpdateManager;
    public type: SystemComponentType = 'service';
    private _updatables: IUpdatable[] = [];
    private _needToRemove: IUpdatable[] = [];
    private _updateFunctions: UpdateFunction[] = [];
    private _timeScale = 1;

    public destroy(): void {
        this.removeAll();
    }

    public update(dt: number): void {
        dt *= this._timeScale;

        this._updatables.forEach(updatable => updatable.isPlaying && updatable.update(dt));
        this._updateFunctions.forEach(callback => callback(dt));

        this.cleanup();
    }

    public add(updatable: IUpdatable): void {
        this._updatables.push(updatable);
        updatable.updateManager = this;
    }

    public remove(updatable: IUpdatable): void {
        const index = this._updatables.indexOf(updatable);

        if (index !== -1) {
            this._needToRemove.push(updatable);
        }
    }

    public has(updatable: IUpdatable): boolean {
        return this._updatables.includes(updatable);
    }

    public addUpdateFunction(updateFunction: UpdateFunction): void {
        this._updateFunctions.push(updateFunction);
    }

    public removeUpdateFunction(updateFunction: UpdateFunction): void {
        const index = this._updateFunctions.indexOf(updateFunction);

        if (index !== -1) {
            this._updateFunctions.splice(index, 1);
        }
    }

    public removeAll(): void {
        this.removeUpdatebleObjects();
        this.removeUpdatebleFunctions();
    }

    public removeUpdatebleObjects(): void {
        this._updatables.forEach(item => item.updateManager = undefined);
        this._needToRemove.forEach(item => item.updateManager = undefined);
        this._updatables.length = 0;
        this._needToRemove.length = 0;
    }

    public removeUpdatebleFunctions(): void {
        this._updateFunctions.length = 0;
    }

    protected cleanup(): void {
        for (const updatable of this._needToRemove) {
            const index = this._updatables.indexOf(updatable);

            if (index !== -1) {
                updatable.updateManager = undefined;
                this._updatables.splice(index, 1);
            }
        }

        this._needToRemove.length = 0;
    }

    public addTimer(callback: Function, duration: number, start?: boolean): Timer {
        const timer = new Timer(callback, duration, start);

        this.add(timer);

        return timer;
    }

    public addTween(object: any): Tween {
        const tween = new Tween(object);

        this.add(tween);

        return tween;
    }

    public addTimerAsync(duration: number, options?: AsyncTimerOptions): Promise<void> {
        let promise = new Promise<void>(res => {
            this.addTimer(res, duration, true);
        });

        if (options?.abortSignal) {
            promise = abortablePromise(promise, options?.abortSignal);
        }

        return promise;
    }

    public get timeScale(): number {
        return this._timeScale;
    }

    public set timeScale(value: number) {
        this._timeScale = Math.max(0, value);
    }
}
